import type { ComponentPropsWithRef } from 'react';
import { cn } from '../lib/cn';

export interface CategoryChipsCategory {
  id: string;
  label: string;
}

export interface CategoryChipsProps extends Omit<ComponentPropsWithRef<'div'>, 'onSelect'> {
  categories: CategoryChipsCategory[];
  /** 선택된 카테고리 id. 없으면 '전체' 선택 상태 */
  selectedId?: string;
  /** '전체' 선택 시 null 전달 */
  onSelect: (id: string | null) => void;
  /** 맨 앞 '전체' 칩 라벨 */
  allLabel: string;
}

const chipBaseClass = cn(
  'inline-flex min-h-11 shrink-0 items-center rounded-sm px-4 text-caption1 font-medium',
  'whitespace-nowrap transition-colors duration-100 select-none',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600',
);

function chipClass(selected: boolean): string {
  return cn(
    chipBaseClass,
    selected ? 'bg-inverse text-fg-inverse' : 'bg-subtle text-fg-secondary hover:bg-neutral-200',
  );
}

export function CategoryChips({
  categories,
  selectedId,
  onSelect,
  allLabel,
  className,
  ...props
}: CategoryChipsProps) {
  const isAllSelected = selectedId === undefined;

  return (
    <div
      className={cn(
        'flex gap-2 overflow-x-auto py-1',
        '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
      {...props}
    >
      <button
        type="button"
        aria-pressed={isAllSelected}
        className={chipClass(isAllSelected)}
        onClick={() => onSelect(null)}
      >
        {allLabel}
      </button>
      {categories.map((category) => {
        const selected = category.id === selectedId;
        return (
          <button
            key={category.id}
            type="button"
            aria-pressed={selected}
            className={chipClass(selected)}
            onClick={() => onSelect(category.id)}
          >
            {category.label}
          </button>
        );
      })}
    </div>
  );
}
